import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { alpha } from "@mui/material/styles";
import { BadgeCheck } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "../ds";
import AttachmentUploader, { type UploadedFile } from "./AttachmentUploader";
import RecipientPicker from "./RecipientPicker";
import {
  RECORD_COLUMN,
  RecordAttachments,
  RecordBlock,
  RecordChips,
  RecordDetails,
  RecordedBy,
  RecordTextPanel,
  type RecordDetail,
} from "./RecordSummary";
import { useIsAuthorized } from "../hooks/useIsAuthorized";
import { invalidateSuggestionCaches, updateSuggestionStatus } from "../api/ess";
import { formatDateTime } from "../utils/format";
import { apiErrorMessage } from "../utils/apiError";
import {
  suggestionEvaluations,
  suggestionImplementation,
  verifierDepartments,
  type Suggestion,
} from "../types/ess";
import type { UserSearchResult } from "../types/auth";

// Closure now sits on `implemented` — the state the backend moves the
// suggestion to on its own once every verifier department has reported
// (see ImplementationCard). `under_verification` is the legacy stop on
// the same road, still tolerated so suggestions that entered it before
// the auto-transition existed can still be closed.
const VERIFICATION_STATES = ["implemented", "under_verification"];
const CLOSED_STATE = "implementation_closed";

// Shape of `metadata.verification`, written server-side by the
// `record_verification` action. Sole consumer is this component, so it's
// kept local rather than added to types/ess.ts (mirrors the "metadata
// over migrations" pattern — no new table/column for a single-field
// structured record).
export interface VerificationRecord {
  benefits_observations: string;
  attachment_ids?: string[] | null;
  verified_by?: string;
  verified_by_name?: string;
  verified_at?: string;
}

export function verificationRecord(
  s: Suggestion,
): VerificationRecord | undefined {
  return (s.metadata as any)?.verification as VerificationRecord | undefined;
}

/**
 * The stored verification, read-only. Exported so the print view renders
 * the same block this card does once the suggestion is closed. Built from
 * the same parts as the evaluation summary: labelled fields, the typed
 * observations in their own panel, attachments, empty fields left out.
 * (Recipients picked on closure are not stored with the record — only as
 * bare ids on `metadata.last_transition` — so the email log is where they
 * show.)
 */
export function VerificationSummary({
  record,
  suggestion,
}: {
  record: VerificationRecord;
  suggestion: Suggestion;
}) {
  // Same three slots as the other records: what was decided, what it
  // covered, who recorded it.
  const departments = verifierDepartments(suggestionEvaluations(suggestion).ssc);
  const done = departments.filter(
    (d) => suggestionImplementation(suggestion).departments?.[d]?.implemented,
  );
  const details: RecordDetail[] = [
    {
      label: "Outcome",
      column: RECORD_COLUMN.subject,
      value: (
        <RecordChips
          labels={[
            suggestion.status === "implementation_closed"
              ? "Implementation closed"
              : "Verified",
          ]}
          tone="success"
        />
      ),
    },
  ];
  if (departments.length > 0) {
    details.push({
      label: "Departments implemented",
      column: RECORD_COLUMN.detail,
      value: `${done.length} of ${departments.length}`,
    });
  }
  if (record.verified_by_name || record.verified_at) {
    details.push({
      label: "Verified by",
      column: RECORD_COLUMN.recordedBy,
      value: (
        <RecordedBy
          name={record.verified_by_name ?? "Unknown"}
          at={record.verified_at ? formatDateTime(record.verified_at) : undefined}
        />
      ),
    });
  }
  return (
    <RecordBlock>
      <RecordDetails items={details} />
      {record.benefits_observations && (
        <RecordTextPanel label="Benefits / observations">
          {record.benefits_observations}
        </RecordTextPanel>
      )}
      <RecordAttachments ids={record.attachment_ids} />
    </RecordBlock>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        })}
      >
        <RavenIcon icon={BadgeCheck} sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", flex: 1 }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

interface Props {
  suggestion: Suggestion;
}

/**
 * Custodian review and closure — the final step of the ESS pipeline.
 * Renders:
 *  - an editor while the suggestion is on `implemented` (or the legacy
 *    `under_verification`) AND the caller passes the `record_verification`
 *    auth probe: a required
 *    "Benefits / observations" note plus attachments, submitting via
 *    `record_verification` (closes the suggestion to
 *    `implementation_closed` server-side);
 *  - a read-only `metadata.verification` block once the suggestion is
 *    `implementation_closed`, for every viewer (the permanent audit
 *    record — mirrors EvaluationFormCard's always-visible summary);
 *  - otherwise nothing (unauthorized mid-flow, or a legacy closed row
 *    with no verification record — e.g. suggestions closed before this
 *    action existed).
 */
export default function VerificationCard({ suggestion }: Props) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const isVerificationState = VERIFICATION_STATES.includes(suggestion.status);
  const isClosed = suggestion.status === CLOSED_STATE;
  const existing = verificationRecord(suggestion);

  const authQ = useIsAuthorized(
    {
      action: "record_verification",
      entity_type: "ess_suggestion",
      entity_id: suggestion.id,
      workflow: "ess_suggestion",
    },
    [suggestion.status],
  );
  const isAuthorizedForAction = authQ.data === true;
  const showEditor = isVerificationState && isAuthorizedForAction;
  const showReadOnly = isClosed && !!existing;

  const [benefitsObservations, setBenefitsObservations] = useState("");
  const [newFiles, setNewFiles] = useState<UploadedFile[]>([]);
  const [recipients, setRecipients] = useState<UserSearchResult[]>([]);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      updateSuggestionStatus(
        suggestion.id,
        "record_verification",
        undefined,
        payload,
      ),
    onSuccess: () => {
      enqueueSnackbar("Verification recorded — suggestion closed", {
        variant: "success",
      });
      invalidateSuggestionCaches(qc, suggestion.id);
      setBenefitsObservations("");
      setNewFiles([]);
      setRecipients([]);
    },
    onError: (err: unknown) => {
      enqueueSnackbar(
        apiErrorMessage(err, "Could not record verification"),
        { variant: "error" },
      );
    },
  });

  if (!showEditor && !showReadOnly) return null;

  const handleSubmit = () => {
    const payload: Record<string, unknown> = {
      benefits_observations: benefitsObservations.trim(),
    };
    const attachmentIds = newFiles.map((f) => f.asset_id);
    if (attachmentIds.length > 0) payload.attachment_ids = attachmentIds;
    if (recipients.length > 0) {
      payload.additional_recipient_user_ids = recipients.map((u) => u.id);
    }
    mutation.mutate(payload);
  };

  return (
    <Card sx={{ mb: 2.5 }} data-tour="detail-verification">
      <CardContent>
        <SectionHeader title="Custodian Review & Closure" />
        {showEditor ? (
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Review the implementation and close the suggestion.
            </Typography>
            <TextField
              label="Benefits / observations"
              required
              multiline
              minRows={3}
              value={benefitsObservations}
              onChange={(e) => setBenefitsObservations(e.target.value)}
            />
            <AttachmentUploader files={newFiles} onChange={setNewFiles} />
            <RecipientPicker value={recipients} onChange={setRecipients} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent={{ xs: "stretch", sm: "flex-end" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={
                  benefitsObservations.trim().length === 0 ||
                  mutation.isPending
                }
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {mutation.isPending
                  ? "Submitting…"
                  : "Review & Close Implementation"}
              </Button>
            </Stack>
          </Stack>
        ) : (
          existing && (
            <VerificationSummary record={existing} suggestion={suggestion} />
          )
        )}
      </CardContent>
    </Card>
  );
}
